import type { AppError } from '@aibi/shared';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Field,
  Input,
  Spinner,
  cn,
} from '@aibi/ui';
import { Check, Copy, ExternalLink, Globe, RefreshCw, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useServices } from '../../app/services';
import { AdminGate } from '../../settings/components/AdminGate';
import { ErrorNotice, Notice } from '../../settings/components/ErrorNotice';
import { useAdminSession, watchUnauthorized } from '../../settings/state/admin-session';
import { AdminSessionProvider } from '../../settings/state/AdminSessionProvider';
import type { WorkspaceDocument } from '../../workspace/model/workspace-document';
import type { QueryDataMode } from '../../workspace/services/query-result-source';
import type { ReportApi, ReportSummary } from '../report-api';
import { reportUrl } from '../report-route';
import {
  clearPublicationSnapshot,
  publicationFormChanged,
  readPublicationSnapshot,
  writePublicationSnapshot,
  type ReportPublicationSnapshot,
} from '../publish-snapshot';

type Publication =
  | { readonly status: 'loading' }
  | { readonly status: 'error'; readonly error: AppError }
  | {
      readonly status: 'ready';
      readonly report: ReportSummary | null;
      readonly snapshot: ReportPublicationSnapshot | null;
    };

const COPIED_FEEDBACK_MS = 2000;
const TITLE_MAX = 200;
const DESCRIPTION_MAX = 1000;

const formatDate = (iso: string): string =>
  new Intl.DateTimeFormat('es', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(iso));

const textareaClassName = cn(
  'flex min-h-20 w-full resize-y rounded-md border border-input bg-background px-2.5 py-2 text-sm transition-colors',
  'placeholder:text-muted-foreground focus-visible:border-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30',
  'disabled:cursor-not-allowed disabled:opacity-50',
);

export interface PublishReportDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly document: WorkspaceDocument;
  readonly dataMode: QueryDataMode | null;
}

/**
 * Publica el analisis actual como reporte de solo lectura y entrega su enlace. Publicar
 * requiere la sesion de administrador; abrir el enlace no.
 */
export function PublishReportDialog({
  open,
  onOpenChange,
  document,
  dataMode,
}: PublishReportDialogProps) {
  const { admin, reports } = useServices();
  if (admin === undefined || reports === undefined) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] flex-col overflow-y-auto p-0">
        <div className="space-y-1.5 px-6 pt-6 pr-10">
          <DialogTitle className="flex items-center gap-2">
            <Globe aria-hidden className="size-4 text-primary" /> Publicar reporte
          </DialogTitle>
          <DialogDescription>
            Genere un enlace para que otras personas vean este analisis como reporte: solo las
            visualizaciones. Pueden explorarlo, pero sus cambios no modifican lo publicado.
          </DialogDescription>
        </div>
        <AdminSessionProvider services={admin}>
          <AdminGate loginDescription="Publicar reportes requiere la contrasena de administrador.">
            <PublishPanel document={document} dataMode={dataMode} reports={reports} />
          </AdminGate>
        </AdminSessionProvider>
      </DialogContent>
    </Dialog>
  );
}

function PublishPanel({
  document,
  dataMode,
  reports,
}: {
  readonly document: WorkspaceDocument;
  readonly dataMode: QueryDataMode | null;
  readonly reports: ReportApi;
}) {
  const session = useAdminSession();
  const { refresh } = session;
  const api = useMemo(() => watchUnauthorized(reports, refresh), [reports, refresh]);
  const [publication, setPublication] = useState<Publication>({ status: 'loading' });
  const [title, setTitle] = useState(document.name);
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState<'publish' | 'remove' | null>(null);
  const [error, setError] = useState<AppError | null>(null);
  const [copied, setCopied] = useState(false);
  const linkRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const effect = { cancelled: false };
    const cancelled = () => effect.cancelled;
    const run = async (): Promise<void> => {
      const listed = await api.list();
      if (cancelled()) return;
      if (!listed.ok) {
        setPublication({ status: 'error', error: listed.error });
        return;
      }
      const summary = listed.value.find((item) => item.workspaceId === document.id) ?? null;
      if (summary === null) {
        setTitle(document.name);
        setDescription('');
        setPublication({ status: 'ready', report: null, snapshot: null });
        return;
      }

      const opened = await api.get(summary.id);
      if (cancelled()) return;
      if (!opened.ok) {
        setPublication({ status: 'error', error: opened.error });
        return;
      }

      const snapshot: ReportPublicationSnapshot = {
        reportId: summary.id,
        name: opened.value.name,
        description: opened.value.description,
        semanticModelId: opened.value.semanticModelId,
        cards: opened.value.cards,
      };
      writePublicationSnapshot(window.localStorage, document.id, snapshot);
      setTitle(opened.value.name);
      setDescription(opened.value.description);
      setPublication({ status: 'ready', report: summary, snapshot });
    };
    void run();
    return () => {
      effect.cancelled = true;
    };
  }, [api, document.id, document.name]);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => {
      setCopied(false);
    }, COPIED_FEEDBACK_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [copied]);

  if (publication.status === 'loading') {
    return (
      <div className="flex justify-center p-6">
        <Spinner label="Comprobando la publicacion" />
      </div>
    );
  }
  if (publication.status === 'error') {
    return (
      <div className="p-6">
        <ErrorNotice error={publication.error} />
      </div>
    );
  }

  const { report, snapshot } = publication;
  const url = report === null ? null : reportUrl(report.id);
  const empty = document.cards.length === 0;
  const trimmedTitle = title.trim();
  const trimmedDescription = description.trim();
  const titleError =
    trimmedTitle === ''
      ? 'Escriba un titulo.'
      : trimmedTitle.length > TITLE_MAX
        ? 'Titulo demasiado largo.'
        : null;
  const descriptionError =
    trimmedDescription.length > DESCRIPTION_MAX ? 'Descripcion demasiado larga.' : null;
  const formValid = titleError === null && descriptionError === null;
  const baseline = snapshot ?? readPublicationSnapshot(window.localStorage, document.id);
  const canPublish =
    !empty &&
    formValid &&
    publicationFormChanged(
      document,
      { name: trimmedTitle, description: trimmedDescription },
      baseline,
    );

  const publish = async () => {
    if (busy !== null || !canPublish) return;
    setBusy('publish');
    setError(null);
    const result = await api.publish(document, report?.id ?? null, {
      name: trimmedTitle,
      description: trimmedDescription,
    });
    setBusy(null);
    if (result.ok) {
      const nextSnapshot: ReportPublicationSnapshot = {
        reportId: result.value.id,
        name: result.value.name,
        description: result.value.description,
        semanticModelId: document.semanticModelId,
        cards: document.cards,
      };
      writePublicationSnapshot(window.localStorage, document.id, nextSnapshot);
      setPublication({ status: 'ready', report: result.value, snapshot: nextSnapshot });
    } else {
      setError(result.error);
    }
  };

  const remove = async () => {
    if (busy !== null || report === null) return;
    setBusy('remove');
    setError(null);
    const result = await api.remove(report.id);
    setBusy(null);
    if (result.ok || result.error.code === 'NOT_FOUND') {
      clearPublicationSnapshot(window.localStorage, document.id);
      setTitle(document.name);
      setDescription('');
      setPublication({ status: 'ready', report: null, snapshot: null });
    } else {
      setError(result.error);
    }
  };

  const copy = async () => {
    if (url === null) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      linkRef.current?.select();
    }
  };

  return (
    <div className="flex flex-col gap-3 px-6 pb-6 pt-4">
      {dataMode === 'preview' && (
        <Notice tone="warning">
          Este analisis usa un modelo de demostracion: el reporte mostrara datos sinteticos, no
          datos reales.
        </Notice>
      )}
      {empty && (
        <Notice tone="info">Agregue al menos una visualizacion para poder publicar.</Notice>
      )}

      <Field
        label="Titulo"
        error={titleError}
        hint={`${String(trimmedTitle.length)}/${String(TITLE_MAX)}`}
      >
        {(control) => (
          <Input
            {...control}
            value={title}
            maxLength={TITLE_MAX}
            disabled={busy !== null}
            placeholder="Nombre que veran quienes abran el enlace"
            onChange={(event) => {
              setTitle(event.target.value);
            }}
          />
        )}
      </Field>
      <Field
        label="Descripcion"
        error={descriptionError}
        hint={`${String(trimmedDescription.length)}/${String(DESCRIPTION_MAX)} · Opcional`}
      >
        {(control) => (
          <textarea
            {...control}
            value={description}
            maxLength={DESCRIPTION_MAX}
            disabled={busy !== null}
            rows={3}
            placeholder="Resuma de que trata este reporte"
            className={textareaClassName}
            onChange={(event) => {
              setDescription(event.target.value);
            }}
          />
        )}
      </Field>

      {report === null || url === null ? (
        <>
          <p className="text-xs text-muted-foreground">
            Este analisis aun no esta publicado. Se generara un enlace con {document.cards.length}{' '}
            visualizacion(es).
          </p>
          <Button
            className="self-start"
            disabled={!canPublish || busy !== null}
            onClick={() => void publish()}
          >
            <Globe /> {busy === 'publish' ? 'Publicando...' : 'Publicar y generar enlace'}
          </Button>
        </>
      ) : (
        <>
          <Field
            label="Enlace del reporte"
            hint={`Publicado el ${formatDate(report.publishedAt)} · Ultima actualizacion ${formatDate(report.updatedAt)} · ${String(report.cardCount)} visualizacion(es)`}
          >
            {(control) => (
              <div className="flex gap-2">
                <Input
                  {...control}
                  ref={linkRef}
                  readOnly
                  value={url}
                  className="font-mono text-xs"
                  onFocus={(event) => {
                    event.currentTarget.select();
                  }}
                />
                <Button variant="outline" size="sm" onClick={() => void copy()}>
                  {copied ? <Check /> : <Copy />} {copied ? 'Copiado' : 'Copiar'}
                </Button>
                <Button asChild variant="outline" size="sm">
                  <a href={url} target="_blank" rel="noopener noreferrer">
                    <ExternalLink /> Abrir
                  </a>
                </Button>
              </div>
            )}
          </Field>
          <p className="text-xs text-muted-foreground">
            {canPublish
              ? 'Hay cambios respecto a lo publicado. Actualice para que el enlace los muestre. El enlace no cambia.'
              : 'No hay cambios que publicar. Edite el analisis, el titulo o la descripcion para actualizar.'}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              disabled={!canPublish || busy !== null}
              onClick={() => void publish()}
            >
              <RefreshCw /> {busy === 'publish' ? 'Actualizando...' : 'Actualizar publicacion'}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-destructive hover:text-destructive"
              disabled={busy !== null}
              onClick={() => void remove()}
            >
              <Trash2 /> {busy === 'remove' ? 'Retirando...' : 'Retirar publicacion'}
            </Button>
          </div>
        </>
      )}

      {error && <ErrorNotice error={error} />}
    </div>
  );
}
